import { EltType, getEltCoverageLabel } from 'legacy/lut/components/widgets/public/partner360/ELT'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button/Button'
import { LoaderOverlay } from 'core-components/library/loader'
import { NotificationController } from 'core-components/library/popup'
import { Controller, isEmpty, ValidateInput } from 'core-components/library/validation'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { buildEltDocumentOpenLink, getEltFormatLabelKey } from '../../../../lib/documentsAndLinks'
import { createEltDocument } from '../../../../lib/documentsAndLinks/api'
import { addCockpitEltDocument } from '../../../../model'

import styles from './EltCreateDialogContent.module.scss'

interface IEltCreateDialogContentProps {
  onCreatingChange?: (isCreating: boolean) => void
  partnerId: string
  fYear: number
  fQuarter: number
  versionSerialNumber: number
  closeDialog: () => void
}

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.documentsAndLinks

export const EltCreateDialogContent = ({
  partnerId,
  fYear,
  fQuarter,
  versionSerialNumber,
  closeDialog,
  onCreatingChange,
}: IEltCreateDialogContentProps) => {
  const dispatch = useAppDispatch()
  const user = useSelector(selectUser)
  const [versionName, setVersionName] = useState(`Version ${versionSerialNumber}`)
  const [eltType, setEltType] = useState<EltType | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const onCreate = async () => {
    if (eltType === null || isSaving) return

    setIsSaving(true)
    onCreatingChange?.(true)

    try {
      const document = await createEltDocument({
        partnerId,
        fYear,
        fQuarter,
        eltType,
        versionName,
        versionSerialNumber,
        userEmail: user.email,
      })

      dispatch(addCockpitEltDocument({ partnerId, document }))
      NotificationController.showSuccess({ message: 'ELT document created successfully.' })
      const documentUrl = buildEltDocumentOpenLink(partnerId, fYear, document.UID)

      window.open(documentUrl, '_blank', 'noopener,noreferrer')
      closeDialog()
    } catch (e) {
      NotificationController.showError({ message: 'Failed to create the ELT document.' })
    } finally {
      setIsSaving(false)
      onCreatingChange?.(false)
    }
  }

  return (
    <Controller>
      <div className={styles.root}>
        <LoaderOverlay show={isSaving} />
        <h3 className={styles.title}>
          <Translation span text={KEYS.sectionElt} /> — {getEltCoverageLabel(fYear, fQuarter)}
        </h3>
        <div className={styles.formatField}>
          <span className={styles.formatLabel}>
            <Translation span text={KEYS.createRow.eltFormatLabel} />
          </span>
          <div className={styles.formatButtons}>
            <Button
              variant={eltType === EltType.Pptx ? 'contained' : 'outlined'}
              onClick={() => setEltType(EltType.Pptx)}
            >
              <Translation span text={getEltFormatLabelKey(EltType.Pptx)} />
            </Button>
            <Button
              variant={eltType === EltType.DocJanuary26 ? 'contained' : 'outlined'}
              onClick={() => setEltType(EltType.DocJanuary26)}
            >
              <Translation span text={getEltFormatLabelKey(EltType.DocJanuary26)} />
            </Button>
          </div>
        </div>
        <ValidateInput checkFn={isEmpty} value={versionName} onChange={setVersionName} />
        <div className={styles.buttons}>
          <Button variant="contained" color="grey" onClick={closeDialog} disabled={isSaving}>
            <Translation span text={KEYS.actions.cancel} />
          </Button>
          <Button
            onClick={onCreate}
            disabled={!versionName || eltType === null || isSaving}
            loading={isSaving}
          >
            <Translation span text={KEYS.createRow.createButton} />
          </Button>
        </div>
      </div>
    </Controller>
  )
}

EltCreateDialogContent.displayName = 'EltCreateDialogContent'
